import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, ShieldCheck, ClipboardText, Translate } from "@phosphor-icons/react";
import { t } from "@/i18n/patientUiStrings";

// 8 languages exposed to the patient. Order matches the sélecteur UI.
const LOCALES = [
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italiano", flag: "🇮🇹" },
  { code: "pt", label: "Português", flag: "🇵🇹" },
  { code: "ar", label: "العربية", flag: "🇸🇦" },
  { code: "zh", label: "中文", flag: "🇨🇳" },
];
const LOCALE_BANNERS = {
  en: "Translation provided for your convenience. The French version of this questionnaire remains the clinical reference.",
  es: "Traducción proporcionada para su comodidad. La versión francesa de este cuestionario sigue siendo la referencia clínica.",
  de: "Übersetzung zur Erleichterung bereitgestellt. Die französische Version dieses Fragebogens bleibt die klinische Referenz.",
  it: "Traduzione fornita per comodità. La versione francese di questo questionario rimane il riferimento clinico.",
  pt: "Tradução fornecida por conveniência. A versão francesa deste questionário continua a ser a referência clínica.",
  ar: "الترجمة مقدمة لراحتك. تظل النسخة الفرنسية من هذا الاستبيان هي المرجع السريري.",
  zh: "此翻译仅供参考,本问卷的法语版本仍是临床参考版本。",
};

// Formats an ISO YYYY-MM-DD birthdate into French DD/MM/YYYY.
function fmtDobFr(iso) {
  if (!iso || typeof iso !== "string" || iso.length < 10) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function Answer({ q, value, onChange, selectedIdx, selectedIdxArr, onPickIdx, onPickIdxArr, locale = "fr" }) {
  if (q.type === "text") {
    return <Textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5] text-lg p-4" data-testid={`answer-${q.id}`} />;
  }
  if (q.type === "number") {
    return <Input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} className="bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5] text-lg p-4 h-14" data-testid={`answer-${q.id}`} />;
  }
  if (q.type === "date") {
    return <Input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="bg-white border-[#E1E9F0] focus-visible:ring-[#3A8FA5] text-lg p-4 h-14" data-testid={`answer-${q.id}`} />;
  }
  if (q.type === "yes_no") {
    const yesLabel = t(locale, "yes");
    const noLabel = t(locale, "no");
    return (
      <div className="grid grid-cols-2 gap-4">
        {[{ v: "yes", label: yesLabel, testKey: "oui" }, { v: "no", label: noLabel, testKey: "non" }].map(({ v, label, testKey }) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            data-testid={`answer-${q.id}-${testKey}`}
            className={`q-card border rounded-2xl py-6 font-serif text-2xl ${value === v ? "border-[#3A8FA5] bg-[#3A8FA5] text-white" : "border-[#E1E9F0] bg-white text-[#1E3A5F]"}`}>
            {label}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === "scale") {
    const min = q.min ?? 0;
    const max = q.max ?? 10;
    const v = value ?? Math.round((min + max) / 2);
    const range = Math.max(1, max - min);
    const pct = ((v - min) / range) * 100;
    const ticks = Array.from({ length: range + 1 }, (_, i) => min + i);
    const isDense = ticks.length > 12;
    return (
      <div className="pt-2">
        <div className="flex items-start justify-between mb-8">
          <div className="flex flex-col text-left">
            <span className="font-serif text-3xl text-[#1E3A5F] leading-none">{min}</span>
            <span className="text-xs uppercase tracking-widest text-[#5F758F] mt-1">
              {q.min_label || t(locale, "minLabel")}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="font-serif text-7xl text-[#3A8FA5] leading-none">{v}</span>
            <span className="text-xs uppercase tracking-widest text-[#5F758F] mt-2">
              Votre choix
            </span>
          </div>
          <div className="flex flex-col text-right">
            <span className="font-serif text-3xl text-[#1E3A5F] leading-none">{max}</span>
            <span className="text-xs uppercase tracking-widest text-[#5F758F] mt-1">
              {q.max_label || t(locale, "maxLabel")}
            </span>
          </div>
        </div>

        <div className="relative px-1 pt-6 pb-3">
          {/* Highly visible gradient track */}
          <div
            className="absolute left-1 right-1 top-1/2 -translate-y-1/2 h-3 rounded-full shadow-inner"
            style={{
              background:
                "linear-gradient(90deg, #3A8FA5 0%, #A7B67A 30%, #E2B15C 65%, #D45A3F 100%)",
            }}
            aria-hidden="true"
          />
          {/* Tick lines under the track */}
          <div className="absolute left-1 right-1 top-1/2 translate-y-3 flex justify-between pointer-events-none">
            {ticks.map((n) => (
              <span
                key={n}
                className={`w-px h-2 ${n <= v ? "bg-[#1E3A5F]/50" : "bg-[#5F758F]/30"}`}
              />
            ))}
          </div>
          {/* Native range input — accessible, full-width, invisible track (we render our own) */}
          <input
            type="range"
            min={min}
            max={max}
            step={1}
            value={v}
            onChange={(e) => onChange(Number(e.target.value))}
            className="scale-slider relative z-10 w-full h-8 appearance-none bg-transparent cursor-pointer"
            data-testid={`answer-${q.id}`}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={v}
            aria-label={q.label}
          />
          {/* Floating value bubble that follows the thumb */}
          <div
            className="absolute -top-1 pointer-events-none transition-all duration-150"
            style={{ left: `calc(${pct}% + ${(50 - pct) * 0.02}rem)`, transform: "translateX(-50%)" }}
            aria-hidden="true"
          >
            <span className="inline-block px-3 py-1 rounded-full bg-[#1E3A5F] text-white text-sm font-serif">
              {v}
            </span>
          </div>
        </div>

        {/* Numbered ticks below the track */}
        <div className="mt-5 flex justify-between px-1">
          {ticks.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={`text-xs md:text-sm tabular-nums transition-colors ${
                n === v
                  ? "font-serif text-lg text-[#1E3A5F]"
                  : "text-[#5F758F] hover:text-[#1E3A5F]"
              } ${isDense ? "px-0.5" : ""}`}
              data-testid={`scale-tick-${q.id}-${n}`}
              aria-label={`Choisir ${n}`}
            >
              {isDense && n !== min && n !== max && n !== v && n % 2 !== 0 ? "·" : n}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (q.type === "single_choice" || q.type === "likert") {
    // Selection must be tracked by INDEX (not by score value) — otherwise two
    // options sharing the same score would both appear selected and Radix's
    // RadioGroup would deduplicate by value.
    // If `selectedIdx` isn't explicitly set (e.g. after a draft rehydration
    // via server), derive it from `value` so the UI still reflects the saved
    // answer.
    const opts = q.options || [];
    let effectiveIdx = selectedIdx;
    if (effectiveIdx == null && value !== undefined && value !== null && value !== "") {
      const derived = opts.findIndex((o) => o.value === value || String(o.value) === String(value));
      if (derived >= 0) effectiveIdx = derived;
    }
    return (
      <RadioGroup value={effectiveIdx != null ? String(effectiveIdx) : ""} onValueChange={(v) => {
        const i = Number(v);
        const opt = opts[i];
        onPickIdx(i);
        onChange(opt?.value ?? v);
      }} className="space-y-3">
        {opts.map((o, i) => (
          <label key={`${q.id}-${i}`} htmlFor={`${q.id}-${i}`}
            className={`q-card flex items-center gap-4 p-5 rounded-2xl border cursor-pointer ${effectiveIdx === i ? "border-[#3A8FA5] bg-[#F4F7FB]" : "border-[#E1E9F0] bg-white"}`}
            data-testid={`answer-${q.id}-opt-${i}`}>
            <RadioGroupItem value={String(i)} id={`${q.id}-${i}`} />
            <span className="text-lg text-[#1E3A5F]">{o.label}</span>
          </label>
        ))}
      </RadioGroup>
    );
  }
  if (q.type === "multiple_choice") {
    const opts = q.options || [];
    let idxArr = Array.isArray(selectedIdxArr) ? selectedIdxArr : null;
    if (!idxArr && Array.isArray(value)) {
      idxArr = value
        .map((vv) => opts.findIndex((o) => o.value === vv || String(o.value) === String(vv)))
        .filter((i) => i >= 0);
    }
    idxArr = idxArr || [];
    const toggle = (i) => {
      let newIdx;
      if (idxArr.includes(i)) newIdx = idxArr.filter((x) => x !== i);
      else newIdx = [...idxArr, i];
      onPickIdxArr(newIdx);
      onChange(newIdx.map((k) => opts[k]?.value).filter((v) => v !== undefined));
    };
    return (
      <div className="space-y-3">
        {opts.map((o, i) => (
          <label key={`${q.id}-mc-${i}`} className={`q-card flex items-center gap-4 p-5 rounded-2xl border cursor-pointer ${idxArr.includes(i) ? "border-[#3A8FA5] bg-[#F4F7FB]" : "border-[#E1E9F0] bg-white"}`}>
            <Checkbox
              checked={idxArr.includes(i)}
              onCheckedChange={() => toggle(i)}
              className="h-5 w-5 rounded-[6px] border-[#B7C4D6] bg-white data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:border-[#3A8FA5] data-[state=checked]:text-white shrink-0"
              data-testid={`answer-${q.id}-mc-${i}`}
            />
            <span className="text-lg text-[#1E3A5F]">{o.label}</span>
          </label>
        ))}
      </div>
    );
  }
  return null;
}

export default function PatientResponse() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [step, setStep] = useState(-2); // -2: consent, -1: intro, 0..N-1: questions, N: contact info
  const [answers, setAnswers] = useState({});
  const [pickedIdx, setPickedIdx] = useState({}); // q.id → int (single/likert)
  const [pickedIdxArr, setPickedIdxArr] = useState({}); // q.id → int[] (multiple)
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [patient, setPatient] = useState({ name: "", email: "" });
  const [done, setDone] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [refused, setRefused] = useState(false);
  const [consentBusy, setConsentBusy] = useState(false);
  const [locale, setLocale] = useState(() => localStorage.getItem("patient_locale") || "fr");
  const [localeLoading, setLocaleLoading] = useState(false);
  const localeMounted = useRef(false);

  // Debounced autosave of partial answers so patients can pause & resume.
  useEffect(() => {
    if (!data || done || refused || step < 0) return;
    if (data.assignment.filled_by_practitioner) return; // no draft in-consultation
    if (Object.keys(answers).length === 0) return;
    const t = setTimeout(async () => {
      try {
        await api.post(`/public/assignment/${token}/draft`, { answers });
        setDraftSavedAt(new Date().toISOString());
      } catch (err) {
        // Draft is best-effort — the beforeunload beacon below is the safety net.
        // We log at debug level so the console isn't spammed on flaky networks.
        if (process.env.NODE_ENV !== "production") console.debug("draft autosave skipped:", err);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [answers, data, done, refused, step, token]);

  // Best-effort flush on tab close / navigation so answers changed within the
  // 900ms debounce window still make it to the server. sendBeacon survives
  // page unload; the fetch fallback covers browsers that block beacons on the
  // preview domain.
  useEffect(() => {
    if (!data || done || refused) return;
    if (data.assignment.filled_by_practitioner) return;
    const flush = () => {
      if (Object.keys(answers).length === 0) return;
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/assignment/${token}/draft`;
      const payload = JSON.stringify({ answers });
      try {
        const blob = new Blob([payload], { type: "application/json" });
        if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return;
      } catch (err) {
        // Beacon constructor failed (rare) — fall through to fetch keepalive.
        if (process.env.NODE_ENV !== "production") console.debug("sendBeacon rejected:", err);
      }
      try {
        fetch(url, { method: "POST", credentials: "include", keepalive: true,
          headers: { "Content-Type": "application/json" }, body: payload });
      } catch (err) {
        // Best-effort final flush — the browser is unloading, nothing to do.
        if (process.env.NODE_ENV !== "production") console.debug("keepalive fetch failed:", err);
      }
    };
    const onHide = () => { if (document.visibilityState === "hidden") flush(); };
    window.addEventListener("pagehide", flush);
    window.addEventListener("beforeunload", flush);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", flush);
      window.removeEventListener("beforeunload", flush);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [answers, data, done, refused, token]);

  useEffect(() => {
    (async () => {
      // First load OR locale change → refetch (backend serves translated template
      // for locale ≠ fr). We preserve draft/step re-hydration ONLY on the very
      // first mount so a mid-questionnaire language switch does NOT wipe
      // the patient's progress.
      const isFirstLoad = !localeMounted.current;
      localeMounted.current = true;
      localStorage.setItem("patient_locale", locale);
      if (!isFirstLoad) setLocaleLoading(true);
      try {
        const { data } = await api.get(`/public/assignment/${token}`, { params: locale && locale !== "fr" ? { locale } : {} });
        setData(data);
        if (!isFirstLoad) return;   // locale switch: keep existing step/answers
        setPatient({ name: data.assignment.patient_name || "", email: "" });
        const draft = data.assignment.draft_answers || {};
        if (Object.keys(draft).length > 0) {
          setAnswers(draft);
          if (data.assignment.draft_saved_at) setDraftSavedAt(data.assignment.draft_saved_at);
          const qs = data.template?.questions || [];
          const idxMap = {};
          const idxArrMap = {};
          qs.forEach((q) => {
            const v = draft[q.id];
            if (v === undefined || v === null || v === "") return;
            const opts = q.options || [];
            if (q.type === "single_choice" || q.type === "likert") {
              const i = opts.findIndex((o) => o.value === v || String(o.value) === String(v));
              if (i >= 0) idxMap[q.id] = i;
            } else if (q.type === "multiple_choice" && Array.isArray(v)) {
              const arr = v
                .map((vv) => opts.findIndex((o) => o.value === vv || String(o.value) === String(vv)))
                .filter((i) => i >= 0);
              if (arr.length) idxArrMap[q.id] = arr;
            }
          });
          if (Object.keys(idxMap).length) setPickedIdx(idxMap);
          if (Object.keys(idxArrMap).length) setPickedIdxArr(idxArrMap);
          const firstUnanswered = qs.findIndex((q) => {
            const v = draft[q.id];
            if (v === undefined || v === null || v === "") return true;
            if (Array.isArray(v) && v.length === 0) return true;
            return false;
          });
          if (firstUnanswered > 0) setStep(firstUnanswered);
        }
        if (data.assignment.status === "completed") {
          setDone(true);
          setCompletedAt(data.assignment.completed_at || null);
        } else if (data.assignment.status === "refused") {
          setRefused(true);
        } else if (data.assignment.consent_given || data.assignment.filled_by_practitioner) {
          setStep(-1);
        }
      } catch (e) {
        const detail = e?.response?.data?.detail;
        if (isFirstLoad) {
          if (detail === "template_deleted" || e?.response?.status === 410) {
            setError({ kind: "template_deleted" });
          } else {
            setError({ kind: "invalid", detail: detail || "Lien invalide" });
          }
        } else {
          toast.error(detail || "Erreur de traduction — retour au français.");
          setLocale("fr");
        }
      } finally {
        setLocaleLoading(false);
      }
    })();
  }, [locale, token]);

  const acceptConsent = async () => {
    setConsentBusy(true);
    try {
      await api.post(`/public/assignment/${token}/consent`, { given: true });
      setStep(-1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setConsentBusy(false); }
  };
  const refuseConsent = async () => {
    if (!window.confirm("Confirmez-vous refuser ? Votre praticien sera notifié.")) return;
    setConsentBusy(true);
    try {
      await api.post(`/public/assignment/${token}/consent`, { given: false });
      setRefused(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setConsentBusy(false); }
  };

  if (error) {
    if (error.kind === "template_deleted") {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-8">
          <div className="max-w-lg text-center" data-testid="template-deleted-msg">
            <ShieldCheck size={64} weight="duotone" className="text-[#3A8FA5] mx-auto mb-6" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1E3A5F] mb-4 leading-tight">
              {t(locale, "templateDeletedTitle")}
            </h1>
            <p className="text-[#5F758F] leading-relaxed">
              {t(locale, "templateDeletedBody")}
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-8">
        <div className="max-w-md text-center">
          <p className="font-serif text-3xl mb-4 text-[#1E3A5F]">{t(locale, "linkInvalid")}</p>
          <p className="text-[#5F758F]">{error.detail}</p>
        </div>
      </div>
    );
  }

  if (refused) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-8">
      <div className="max-w-lg text-center">
        <ShieldCheck size={64} weight="duotone" className="text-[#5F758F] mx-auto mb-6" />
        <h1 className="text-4xl font-bold tracking-tight text-[#1E3A5F] mb-4">{t(locale, "consentRefusedTitle")}</h1>
        <p className="text-[#5F758F]">{t(locale, "consentRefusedBody")}</p>
      </div>
    </div>
  );

  if (!data) return null;

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F7FB] p-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg text-center">
        <CheckCircle size={64} weight="duotone" className="text-[#3A8FA5] mx-auto mb-6" />
        <h1 className="font-serif text-5xl mb-6 text-[#1E3A5F]">{t(locale, "thanks")}</h1>
        <p className="text-lg text-[#5F758F]">{t(locale, "thanksBody")}</p>
        {completedAt && (
          <p className="text-sm text-[#5F758F] mt-6" data-testid="completed-at">
            {t(
              locale,
              "completedAt",
              new Date(completedAt).toLocaleDateString(locale === "fr" ? "fr-FR" : locale, { day: "numeric", month: "short", year: "numeric" }),
              new Date(completedAt).toLocaleTimeString(locale === "fr" ? "fr-FR" : locale, { hour: "2-digit", minute: "2-digit" }),
            )}
          </p>
        )}
      </motion.div>
    </div>
  );

  const { template, assignment } = data;
  const isDirect = !!assignment.filled_by_practitioner;
  const questions = template.questions;
  const total = questions.length;

  const current = step >= 0 && step < total ? questions[step] : null;
  const progress = step < 0 ? 0 : step >= total ? 100 : ((step + 1) / (total + 1)) * 100;

  const canNext = () => {
    if (!current) return true;
    if (!current.required) return true;
    const v = answers[current.id];
    if (v === undefined || v === null || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/assignment/${token}/submit`, {
        answers, patient_name: patient.name, patient_email: patient.email || null,
      });
      setCompletedAt(data?.completed_at || new Date().toISOString());
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FB] relative">
      <div className="fixed top-0 left-0 right-0 h-1 bg-[#E1E9F0] z-40">
        <div className="h-full bg-[#3A8FA5] transition-all duration-500" style={{ width: `${progress}%` }} />
      </div>

      <header className="pt-8 pb-4 px-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-[10px] bg-[#3A8FA5] flex items-center justify-center text-white font-bold text-base">Q</span>
          <span className="text-[18px] font-bold tracking-tight text-[#1E3A5F]">Questiomed</span>
        </div>
        <div className="relative">
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            disabled={localeLoading}
            data-testid="patient-locale-selector"
            aria-label="Language / Langue"
            className="appearance-none bg-white border border-[#E1E9F0] rounded-full pl-9 pr-8 py-1.5 text-[13px] font-semibold text-[#1E3A5F] hover:border-[#C7DEE6] focus:outline-none focus:ring-2 focus:ring-[#3A8FA5] cursor-pointer disabled:opacity-60"
          >
            {LOCALES.map((l) => (
              <option key={l.code} value={l.code}>{l.flag}  {l.label}</option>
            ))}
          </select>
          <Translate size={14} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#3A8FA5] pointer-events-none" />
          {localeLoading && (
            <span className="absolute -bottom-5 right-0 text-[10px] text-[#5F758F] italic">{t(locale, "translating")}</span>
          )}
        </div>
      </header>
      {locale !== "fr" && LOCALE_BANNERS[locale] && (
        <div className="mx-auto max-w-2xl px-6" data-testid="translation-banner">
          <div className="bg-[#FFF3D6] border border-[#F0DDA8] text-[#8A6900] rounded-[10px] px-4 py-2.5 text-[13px] leading-relaxed" dir={locale === "ar" ? "rtl" : "ltr"}>
            <strong>ℹ️</strong> {LOCALE_BANNERS[locale]}
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-6 py-12 md:py-24">
        <AnimatePresence mode="wait">
          {step === -2 && (
            <motion.div key="consent" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E6F1F5] border border-[#C7DEE6] text-[#3A8FA5] text-[11px] tracking-[0.12em] uppercase font-bold mb-5">
                <ClipboardText size={14} weight="duotone" /> {t(locale, "consentEyebrow")}
              </div>
              <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-[#1E3A5F] leading-[1.15] mb-4">
                <span className="text-[#3A8FA5]">{assignment.practitioner_name}</span> {t(locale, "consentTitleSuffix")}
              </h1>
              <p className="text-lg text-[#5F758F] leading-relaxed mb-8">
                {t(locale, "consentSubtitle")}
              </p>

              <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck size={22} weight="duotone" className="text-[#3A8FA5] shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[13px] font-bold text-[#1E3A5F] mb-0.5">{t(locale, "dataProtectedTitle")}</p>
                    <p className="text-[13px] text-[#5F758F] leading-relaxed">
                      {t(locale, "dataProtectedBody")}
                    </p>
                  </div>
                </div>
                <label
                  htmlFor="rgpd-consent"
                  className={`flex items-start gap-3 p-4 rounded-[14px] border-2 cursor-pointer transition-colors ${consentGiven ? "border-[#3A8FA5] bg-[#E6F1F5]" : "border-[#E1E9F0] bg-white hover:border-[#3A8FA5]/50"}`}
                >
                  <Checkbox
                    id="rgpd-consent"
                    checked={consentGiven}
                    onCheckedChange={(v) => setConsentGiven(!!v)}
                    className="mt-0.5 h-5 w-5 border-2 border-[#3A8FA5] data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:border-[#3A8FA5] data-[state=checked]:text-white"
                    data-testid="rgpd-consent-checkbox"
                  />
                  <p className="text-[14px] text-[#1E3A5F] leading-relaxed flex-1">
                    {(() => {
                      const raw = t(locale, "consentCheckboxLabel");
                      const [before, after] = raw.split("{privacy}");
                      return (
                        <>
                          {before}
                          <a href="/confidentialite" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#3A8FA5] underline underline-offset-2 font-semibold hover:text-[#164E5F]" data-testid="privacy-link">{t(locale, "privacyLinkLabel")}</a>
                          {after}
                        </>
                      );
                    })()}
                  </p>
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="outline"
                  onClick={refuseConsent}
                  disabled={consentBusy}
                  className="rounded-[10px] font-semibold border-[#E1E9F0] text-[#5F758F] hover:bg-[#F4F7FB]"
                  data-testid="consent-refuse-btn"
                >
                  {t(locale, "refuse")}
                </Button>
                <Button
                  size="lg"
                  onClick={acceptConsent}
                  disabled={!consentGiven || consentBusy}
                  className="rounded-[12px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold px-8 py-6 shadow-[0_4px_12px_rgba(58,143,165,0.25)]"
                  data-testid="consent-accept-btn"
                >
                  {consentBusy ? "…" : t(locale, "acceptAndContinue")}
                </Button>
              </div>
              <p className="text-[12px] text-[#5F758F] mt-5 leading-relaxed">
                {t(locale, "consentFooter")}
              </p>
            </motion.div>
          )}

          {step === -1 && (
            <motion.div key="intro" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-6">{t(locale, "introEyebrow")}</p>
              <h1 className="font-serif text-4xl md:text-5xl mb-6 text-[#1E3A5F] leading-tight">{template.title}</h1>
              <p className="text-lg text-[#5F758F] leading-relaxed mb-8">
                {isDirect
                  ? (() => {
                      const parts = t(locale, "introDirect", assignment.practitioner_name, `${assignment.patient_name}${assignment.patient_date_of_birth ? ` (${fmtDobFr(assignment.patient_date_of_birth)})` : ""}`);
                      return (
                        <>
                          {parts[0]}<span className="text-[#3A8FA5] font-semibold">{parts[1]}</span>{parts[2]}{parts[3]}{parts[4]}<span className="text-[#1E3A5F] font-medium">{parts[5]}</span>{parts[6]}
                        </>
                      );
                    })()
                  : (() => {
                      const parts = t(locale, "introRemote", assignment.practitioner_name);
                      return (
                        <>
                          {parts[0]}<span className="text-[#1E3A5F] font-medium">{parts[1]}</span>{parts[2]}
                        </>
                      );
                    })()}
              </p>
              {template.instructions && <p className="text-lg text-[#5F758F] italic mb-12 leading-relaxed">{template.instructions}</p>}
              <p className="text-sm text-[#5F758F] mb-4">{t(locale, "questionsCount", total)} · {t(locale, "minutes", Math.max(1, Math.ceil(total / 3)))}</p>

              {!isDirect && Object.keys(answers).length > 0 && (
                <div className="mb-6 rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] px-4 py-3 text-[13px] text-[#1E3A5F]" data-testid="resume-banner">
                  <p className="font-semibold mb-0.5">{t(locale, "resumeTitle")}</p>
                  {t(locale, "resumeBody", Object.keys(answers).length)}
                </div>
              )}
              {!isDirect && (
                <div className="mb-6 rounded-[10px] bg-[#F4F7FB] border border-[#E1E9F0] px-4 py-3 text-[13px] text-[#5F758F]" data-testid="pause-tip">
                  <strong className="text-[#1E3A5F]">{t(locale, "pauseTipStrong")}</strong> {t(locale, "pauseTipBody")}
                </div>
              )}

              {template.consent_required && !isDirect && (
                <label
                  htmlFor="consent"
                  className={`hidden flex items-start gap-4 p-5 rounded-2xl border cursor-pointer mb-8 transition-colors ${consentGiven ? "border-[#3A8FA5] bg-[#F4F7FB]" : "border-[#E1E9F0] bg-white"}`}
                >
                  <Checkbox
                    id="consent"
                    checked={consentGiven}
                    onCheckedChange={(v) => setConsentGiven(!!v)}
                    className="mt-1 h-6 w-6 border-2 border-[#3A8FA5] data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:border-[#3A8FA5] data-[state=checked]:text-white [&_svg]:h-5 [&_svg]:w-5 [&_svg]:stroke-[3]"
                    data-testid="consent-checkbox"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <ShieldCheck size={16} weight="duotone" className="text-[#3A8FA5]" />
                      <span className="text-xs tracking-[0.2em] uppercase text-[#3A8FA5]">Consentement RGPD</span>
                    </div>
                    <p className="text-sm text-[#1E3A5F] leading-relaxed">
                      {template.consent_text || "J'accepte que mes réponses soient transmises à mon praticien dans le cadre de ma prise en charge médicale."}
                    </p>
                    <a
                      href="/confidentialite"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#3A8FA5] underline underline-offset-2 mt-2 inline-block"
                      data-testid="privacy-link"
                    >
                      Lire la politique de confidentialité complète →
                    </a>
                  </div>
                </label>
              )}

              <label
                htmlFor="terms"
                className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer mb-8 transition-colors ${termsAccepted ? "border-[#3A8FA5] bg-[#F4F7FB]" : "border-[#E1E9F0] bg-white"} ${isDirect ? "hidden" : ""}`}
              >
                <Checkbox
                  id="terms"
                  checked={termsAccepted}
                  onCheckedChange={(v) => setTermsAccepted(!!v)}
                  className="mt-1 h-6 w-6 border-2 border-[#3A8FA5] data-[state=checked]:bg-[#3A8FA5] data-[state=checked]:border-[#3A8FA5] data-[state=checked]:text-white [&_svg]:h-5 [&_svg]:w-5 [&_svg]:stroke-[3]"
                  data-testid="terms-checkbox"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs tracking-[0.2em] uppercase text-[#3A8FA5]">{t(locale, "cguEyebrow")}</span>
                  </div>
                  <p className="text-sm text-[#1E3A5F] leading-relaxed">
                    {t(locale, "cguText")}
                  </p>
                  <a
                    href="/cgu"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-[#3A8FA5] underline underline-offset-2 mt-2 inline-block"
                    data-testid="cgu-link"
                  >
                    {t(locale, "readCgu")}
                  </a>
                </div>
              </label>

              <Button
                size="lg"
                onClick={() => setStep(0)}
                disabled={!isDirect && !termsAccepted}
                className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white px-10 py-7"
                data-testid="start-btn"
              >
                {t(locale, "start")}
              </Button>
            </motion.div>
          )}

          {current && (
            <motion.div key={`q-${step}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              {current.instructions && (
                <div className="mb-8 border-l-2 border-[#D45A3F] pl-4 py-1" data-testid="q-instructions">
                  <p className="text-xs tracking-[0.2em] uppercase text-[#D45A3F] mb-1.5">{t(locale, "instructionsForThisQuestion")}</p>
                  <p className="text-base text-[#5F758F] italic leading-relaxed">{current.instructions}</p>
                </div>
              )}
              <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">{t(locale, "questionOf", step + 1, total)}</p>
              <h2 className="font-serif text-2xl md:text-3xl mb-10 text-[#1E3A5F] leading-tight">
                {current.label}{current.required && <span className="text-[#D45A3F]"> *</span>}
              </h2>
              {!isDirect && draftSavedAt && (
                <div className="mb-4 inline-flex items-center gap-1.5 text-[12px] text-[#3A8FA5] bg-[#E6F1F5] border border-[#C7DEE6] rounded-full px-3 py-1" data-testid="draft-saved-badge">
                  <CheckCircle size={12} weight="fill" />
                  {t(locale, "draftSaved")}
                </div>
              )}
              <div className="mb-12">
                <Answer
                  q={current}
                  locale={locale}
                  value={answers[current.id]}
                  onChange={(v) => setAnswers((a) => ({ ...a, [current.id]: v }))}
                  selectedIdx={pickedIdx[current.id]}
                  selectedIdxArr={pickedIdxArr[current.id]}
                  onPickIdx={(i) => setPickedIdx((p) => ({ ...p, [current.id]: i }))}
                  onPickIdxArr={(arr) => setPickedIdxArr((p) => ({ ...p, [current.id]: arr }))}
                />
              </div>
              <div className="flex justify-between items-center gap-3">
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full h-11 px-5 text-[15px]">{t(locale, "previous")}</Button>
                <Button size="lg" onClick={() => setStep(step + 1)} disabled={!canNext()} className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white h-12 px-8 text-[15px] flex-1 sm:flex-initial" data-testid="next-btn">
                  {step === total - 1 ? t(locale, "finishStep") : t(locale, "next")}
                </Button>
              </div>
            </motion.div>
          )}

          {step === total && (
            <motion.div key="contact" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <p className="text-xs tracking-[0.3em] uppercase text-[#3A8FA5] mb-4">{t(locale, "lastStep")}</p>
              <h2 className="font-serif text-4xl mb-4 text-[#1E3A5F]">{isDirect ? t(locale, "saving") : t(locale, "almostThere")}</h2>
              <p className="text-[#5F758F] mb-8">
                {isDirect
                  ? t(locale, "saveIntroDirect", assignment.patient_name)
                  : t(locale, "submitIntro")}
              </p>
              <div className="flex justify-between gap-3">
                <Button variant="ghost" onClick={() => setStep(step - 1)} className="rounded-full h-11 px-5 text-[15px]">{t(locale, "previous")}</Button>
                <Button size="lg" onClick={submit} disabled={busy} className="rounded-full bg-[#3A8FA5] hover:bg-[#164E5F] text-white h-12 px-8 text-[15px] flex-1 sm:flex-initial" data-testid="submit-btn">
                  {busy ? t(locale, "submitting") : (isDirect ? t(locale, "saveScore") : t(locale, "submit"))}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
